import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type ProductionAndConsumptionFilterContextValue = {
  textFilter: string;
  debouncedTextFilter: string;
  unmatchedOnly: boolean;
  setTextFilter: React.Dispatch<React.SetStateAction<string>>;
  setUnmatchedOnly: React.Dispatch<React.SetStateAction<boolean>>;
  resetFilters: () => void;
};

const ProductionAndConsumptionFilterContext =
  createContext<ProductionAndConsumptionFilterContextValue | null>(null);

type ProductionAndConsumptionFilterProviderProps = {
  children: React.ReactNode;
};

export const ProductionAndConsumptionFilterProvider: React.FC<
  ProductionAndConsumptionFilterProviderProps
> = ({ children }) => {
  const [textFilter, setTextFilter] = useState('');
  const [debouncedTextFilter, setDebouncedTextFilter] = useState('');
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedTextFilter(textFilter.toLowerCase());
    }, 500);

    return () => clearTimeout(timeout);
  }, [textFilter]);

  const resetFilters = useCallback(() => {
    setTextFilter('');
    setDebouncedTextFilter('');
    setUnmatchedOnly(false);
  }, []);

  const value = useMemo(
    () => ({
      textFilter,
      debouncedTextFilter,
      unmatchedOnly,
      setTextFilter,
      setUnmatchedOnly,
      resetFilters,
    }),
    [textFilter, debouncedTextFilter, unmatchedOnly, resetFilters]
  );

  return (
    <ProductionAndConsumptionFilterContext.Provider value={value}>
      {children}
    </ProductionAndConsumptionFilterContext.Provider>
  );
};

export const useProductionAndConsumptionFilterContext =
  (): ProductionAndConsumptionFilterContextValue => {
    const context = useContext(ProductionAndConsumptionFilterContext);

    if (!context) {
      throw new Error(
        'useProductionAndConsumptionFilterContext must be used within ProductionAndConsumptionFilterProvider'
      );
    }

    return context;
  };
